import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Checkbox, Spin, Input } from 'antd';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import cn from 'classnames';
import './styles.scss';
import { FormattedMessage, useIntl } from 'react-intl';

const MAX_SELECTION_DEFAULT = 5;

const mapCategoryNode = (node, parentId = null) => ({
  id: node.id,
  name: node.name,
  parentId,
  children: (node.childCategories || []).map((child) =>
    mapCategoryNode(child, node.id)
  ),
});

const normalizeCategories = (categories) =>
  (categories || []).map((parent) => mapCategoryNode(parent));

const collectIds = (nodes, set = new Set()) => {
  nodes.forEach((node) => {
    set.add(node.id);
    if (node.children.length) {
      collectIds(node.children, set);
    }
  });
  return set;
};

const buildParentLookup = (nodes, map = new Map()) => {
  nodes.forEach((node) => {
    node.children.forEach((child) => {
      map.set(child.id, node.id);
    });
    buildParentLookup(node.children, map);
  });
  return map;
};

const filterTree = (nodes, term, getLocalizedName) => {
  if (!term) return nodes;

  const lowerTerm = term.toLowerCase();

  return nodes.reduce((acc, node) => {
    const filteredChildren = filterTree(node.children, term, getLocalizedName);
    const originalName = node.name.toLowerCase();
    const localizedName = getLocalizedName
      ? getLocalizedName(node.name).toLowerCase()
      : '';
    const nodeMatches =
      originalName.includes(lowerTerm) || localizedName.includes(lowerTerm);

    if (nodeMatches) {
      acc.push({ ...node, children: node.children });
    } else if (filteredChildren.length) {
      acc.push({ ...node, children: filteredChildren });
    }

    return acc;
  }, []);
};

const gatherParentIds = (nodes, set = new Set()) => {
  nodes.forEach((node) => {
    if (node.children.length) {
      set.add(node.id);
      gatherParentIds(node.children, set);
    }
  });
  return set;
};

const collectDescendantIds = (node, acc = []) => {
  node.children.forEach((child) => {
    acc.push(child.id);
    collectDescendantIds(child, acc);
  });
  return acc;
};

const IabCategorySelector = ({
  categories,
  value = [],
  onChange,
  maxSelection = MAX_SELECTION_DEFAULT,
  loading = false,
  error,
  onRetry,
  disabled = false,
  hideParentCheckbox = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedParentIds, setExpandedParentIds] = useState([]);
  const intl = useIntl();

  const normalizedCategories = useMemo(
    () => normalizeCategories(categories),
    [categories]
  );

  const validIds = useMemo(
    () => collectIds(normalizedCategories),
    [normalizedCategories]
  );

  const parentLookup = useMemo(
    () => buildParentLookup(normalizedCategories),
    [normalizedCategories]
  );

  useEffect(() => {
    if (!Array.isArray(value)) return;

    const sanitized = value.filter((id) => validIds.has(id));
    if (sanitized.length !== value.length) {
      onChange?.(sanitized);
    }
  }, [value, validIds, onChange]);

  const selectionSet = useMemo(() => new Set(value || []), [value]);

  const nameToKey = (name) => {
    return `advertiser_modal.iab_category.${name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')}`;
  };

  const getLocalizedCategoryName = (name) => {
    const key = nameToKey(name);
    const translated = intl.formatMessage({ id: key });
    return translated !== key ? translated : name;
  };

  const filteredCategories = useMemo(() => {
    const trimmed = searchTerm.trim();
    if (!trimmed) return normalizedCategories;
    return filterTree(normalizedCategories, trimmed, getLocalizedCategoryName);
  }, [normalizedCategories, searchTerm, intl]);

  useEffect(() => {
    if (!searchTerm) return;

    const matchedParentIds = Array.from(gatherParentIds(filteredCategories));
    setExpandedParentIds((prev) => {
      const merged = new Set(prev);
      matchedParentIds.forEach((id) => merged.add(id));
      return Array.from(merged);
    });
  }, [searchTerm, filteredCategories]);

  useEffect(() => {
    if (!normalizedCategories.length) {
      setExpandedParentIds([]);
    } else if (hideParentCheckbox) {
      // Auto-expand all parent categories when hiding parent checkbox
      const allParentIds = normalizedCategories
        .filter((cat) => cat.children && cat.children.length > 0)
        .map((cat) => cat.id);
      setExpandedParentIds(allParentIds);
    }
  }, [normalizedCategories.length, hideParentCheckbox, normalizedCategories]);

  const isLimitReached = !disabled && selectionSet.size >= maxSelection;

  const commitSelection = (nextSet) => {
    const nextValue = Array.from(nextSet);
    if (
      nextValue.length === value.length &&
      nextValue.every((id) => selectionSet.has(id))
    ) {
      return;
    }
    onChange?.(nextValue);
  };

  const handleParentCheck = (node, checked) => {
    const nextSet = new Set(selectionSet);
    const descendantIds = collectDescendantIds(node);

    if (checked) {
      if (!selectionSet.has(node.id) && isLimitReached) return;
      nextSet.add(node.id);
      descendantIds.forEach((id) => nextSet.delete(id));
    } else {
      nextSet.delete(node.id);
    }

    commitSelection(nextSet);
  };

  const handleChildCheck = (node, checked) => {
    if (isAncestorSelected(node.id)) return;

    const nextSet = new Set(selectionSet);

    if (checked) {
      if (!selectionSet.has(node.id) && isLimitReached) return;
      nextSet.add(node.id);
    } else {
      nextSet.delete(node.id);
    }

    commitSelection(nextSet);
  };

  const toggleParentExpansion = (parentId) => {
    setExpandedParentIds((prev) =>
      prev.includes(parentId)
        ? prev.filter((id) => id !== parentId)
        : [...prev, parentId]
    );
  };

  const isAncestorSelected = (nodeId) => {
    let currentId = nodeId;
    while (parentLookup.has(currentId)) {
      const parentId = parentLookup.get(currentId);
      if (selectionSet.has(parentId)) {
        return true;
      }
      currentId = parentId;
    }
    return false;
  };

  const renderNodes = renderNodesFactory({
    selectionSet,
    toggleParentExpansion,
    expandedParentIds,
    handleParentCheck,
    handleChildCheck,
    disabled,
    isLimitReached,
    isAncestorSelected,
    hideParentCheckbox,
  });

  const handleClearSearch = () => setSearchTerm('');

  const showEmptyState = !loading && !error && !normalizedCategories.length;
  const showNoMatches =
    !loading &&
    !error &&
    normalizedCategories.length > 0 &&
    !filteredCategories.length;

  return (
    <div className={cn('iab-category-selector', { disabled })}>
      <div className="iab-category-selector__header">
        <Input
          className="iab-category-selector__input"
          allowClear
          placeholder={intl.formatMessage({
            id: 'advertiser_modal.search_iab_categories',
          })}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value || '')}
          onPressEnter={(event) => event.preventDefault()}
        />
        <span className="iab-category-selector__counter">
          <FormattedMessage
            id="advertiser_modal.selected_iab_categories"
            values={{
              selected: selectionSet.size,
              max: maxSelection,
            }}
          />
        </span>
      </div>

      {loading && (
        <div className="iab-category-selector__status">
          <Spin size="small" />
          <span>
            <FormattedMessage id="advertiser_modal.loading_categories" />
          </span>
        </div>
      )}

      {error && !loading && (
        <div className="iab-category-selector__status is-error" role="alert">
          <span className="iab-category-selector__status-message">
            {error?.message || error?.Message || (
              <FormattedMessage id="advertiser_modal.unable_to_load_categories" />
            )}
          </span>
          {onRetry && (
            <Button size="small" onClick={onRetry}>
              <FormattedMessage id="advertiser_modal.retry" />
            </Button>
          )}
        </div>
      )}

      {showEmptyState && (
        <div className="iab-category-selector__empty">
          <FormattedMessage id="advertiser_modal.no_categories_available" />
        </div>
      )}

      {showNoMatches && (
        <div className="iab-category-selector__empty">
          <FormattedMessage
            id="advertiser_modal.no_categories_found"
            values={{ searchTerm }}
          />
          <Button
            type="link"
            size="small"
            className="iab-category-selector__clear-search"
            onClick={handleClearSearch}
          >
            <FormattedMessage id="advertiser_modal.clear_search" />
          </Button>
        </div>
      )}

      {!loading && !error && filteredCategories.length > 0 && (
        <ul className="iab-category-selector__tree" role="tree">
          {hideParentCheckbox
            ? filteredCategories.map((parentCategory) =>
                parentCategory.children.length > 0 ? (
                  <React.Fragment key={parentCategory.id}>
                    {renderNodes(parentCategory.children, 0)}
                  </React.Fragment>
                ) : null
              )
            : renderNodes(filteredCategories)}
        </ul>
      )}
    </div>
  );
};

const renderNodesFactory = ({
  selectionSet,
  toggleParentExpansion,
  expandedParentIds,
  handleParentCheck,
  handleChildCheck,
  disabled,
  isLimitReached,
  isAncestorSelected,
  hideParentCheckbox = false,
}) => {
  const intl = useIntl();
  const nameToKey = (name) => {
    return `advertiser_modal.iab_category.${name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')}`;
  };

  const getLocalizedCategoryName = (name, intl) => {
    const key = nameToKey(name);
    const translated = intl.formatMessage({ id: key });
    return translated !== key ? translated : name;
  };
  const renderNodes = (nodes, level = 0) =>
    nodes.map((node) => {
      const localizedName = getLocalizedCategoryName(node.name, intl);

      const hasChildren = node.children.length > 0;
      const nodeChecked = selectionSet.has(node.id);
      const ancestorSelected = isAncestorSelected(node.id);
      const isExpanded = hasChildren
        ? expandedParentIds.includes(node.id)
        : false;

      const nodeDisabled =
        disabled || ancestorSelected || (isLimitReached && !nodeChecked);

      const commonRowProps = {
        className: 'iab-category-selector__parent-row',
        style: {
          '--tree-indent-level': level,
        },
      };

      // Hide parent checkbox if hideParentCheckbox is true and this is a parent node
      if (hideParentCheckbox && hasChildren && level === 0) {
        // Render only children, always expanded
        return (
          <React.Fragment key={node.id}>
            {renderNodes(node.children, 0)}
          </React.Fragment>
        );
      }

      const checkbox = (
        <Checkbox
          checked={nodeChecked}
          onChange={(event) =>
            hasChildren
              ? handleParentCheck(node, event.target.checked)
              : handleChildCheck(node, event.target.checked)
          }
          disabled={nodeDisabled}
        >
          {localizedName}
        </Checkbox>
      );

      return (
        <li
          key={node.id}
          className={cn('iab-category-selector__parent', {
            'iab-category-selector__child': level > 0,
            'is-disabled': nodeDisabled,
          })}
          role="treeitem"
          aria-expanded={hasChildren ? isExpanded : undefined}
          aria-level={level + 1}
        >
          <div {...commonRowProps}>
            {hasChildren ? (
              <button
                type="button"
                className="iab-category-selector__caret"
                aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${
                  node.name
                }`}
                onClick={() => toggleParentExpansion(node.id)}
              >
                {isExpanded ? <DownOutlined /> : <RightOutlined />}
              </button>
            ) : (
              <span className="iab-category-selector__caret-placeholder" />
            )}
            {checkbox}
          </div>

          {hasChildren && isExpanded && (
            <ul className="iab-category-selector__children" role="group">
              {renderNodes(node.children, level + 1)}
            </ul>
          )}
        </li>
      );
    });

  return renderNodes;
};

IabCategorySelector.propTypes = {
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      name: PropTypes.string.isRequired,
      childCategories: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
            .isRequired,
          name: PropTypes.string.isRequired,
        })
      ),
    })
  ),
  value: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  ),
  onChange: PropTypes.func,
  maxSelection: PropTypes.number,
  loading: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  onRetry: PropTypes.func,
  disabled: PropTypes.bool,
  hideParentCheckbox: PropTypes.bool,
};

export default React.memo(IabCategorySelector);
